import { MA_CATS, type Venue } from "@myams/shared";
import { MaIcon } from "./MaIcon";
import { MaImageSlot } from "./MaImageSlot";
import { MaLiveBadge } from "./MaLiveBadge";

interface Props {
  venue: Venue;
  variant?: "default" | "mini";
  distance?: string;
  friends?: string[];
  onClick?: () => void;
}

export function MaVenueCard({ venue, variant = "default", distance, friends, onClick }: Props) {
  const cat = MA_CATS[venue.cat] ?? MA_CATS.all;

  if (variant === "mini") {
    return (
      <button onClick={onClick} style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: 10, background: "var(--ma-cream)",
        border: "var(--ma-border) solid var(--ma-ink)",
        borderRadius: "var(--ma-card-radius)",
        width: "100%", textAlign: "left", cursor: onClick ? "pointer" : "default",
      }}>
        <div style={{ width: 56, flexShrink: 0 }}>
          <MaImageSlot
            height={56}
            tone={venue.cat === "music" ? "brick" : "mustard"}
            src={venue.coverUrl}
            category={venue.cat}
            fallbackInitial={venue.name}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ma-ink)" }}>{venue.name}</div>
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 2 }}>
            {cat.label} · {venue.neigh}
          </div>
        </div>
        {venue.live && <span className="ma-pulse-dot" />}
      </button>
    );
  }

  return (
    <button onClick={onClick} style={{
      background: "var(--ma-cream)",
      border: "var(--ma-border) solid var(--ma-ink)",
      borderRadius: "var(--ma-card-radius)",
      overflow: "hidden", position: "relative",
      width: "100%", textAlign: "left", padding: 0, cursor: onClick ? "pointer" : "default",
    }}>
      <div style={{ position: "relative" }}>
        <MaImageSlot
          height={140}
          tone={venue.cat === "music" ? "brick" : venue.cat === "art" ? "canal" : venue.cat === "queer" ? "pink" : "mustard"}
          src={venue.coverUrl}
          category={venue.cat}
          fallbackInitial={venue.name}
        />
        {venue.live && (
          <div style={{ position: "absolute", top: 10, left: 10 }}>
            <MaLiveBadge />
          </div>
        )}
        {distance && (
          <div style={{
            position: "absolute", top: 10, right: 10,
            padding: "4px 8px", background: "var(--ma-ink)", color: "var(--ma-paper)",
            fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.06em",
          }}>{distance}</div>
        )}
      </div>
      <div style={{ padding: "12px 14px 14px" }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.14em",
          textTransform: "uppercase", color: cat.color, marginBottom: 4,
        }}>
          {cat.label} · {venue.neigh}
        </div>
        <div className="ma-display" style={{ fontSize: 22, color: "var(--ma-ink)", marginBottom: 8 }}>
          {venue.name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ma-ink-soft)" }}>
          <MaIcon name="clock" size={13} stroke={1.6} />
          <span>{venue.now}</span>
          <span style={{ marginLeft: "auto" }} className="ma-mono">{venue.price}</span>
        </div>
        {friends && friends.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 6, marginTop: 10,
            paddingTop: 10, borderTop: "1px dashed var(--ma-line)",
          }}>
            <div style={{ display: "flex" }}>
              {friends.slice(0, 3).map((_, i) => (
                <div key={i} style={{
                  width: 18, height: 18, borderRadius: "50%",
                  background: ["var(--ma-brick)", "var(--ma-canal)", "var(--ma-mustard)"][i % 3],
                  border: "1.5px solid var(--ma-cream)",
                  marginLeft: i ? -6 : 0,
                }} />
              ))}
            </div>
            <span className="ma-mono" style={{
              fontSize: 10, color: "var(--ma-ink-soft)",
              letterSpacing: "0.08em", textTransform: "uppercase",
            }}>
              {friends.length === 1 ? `${friends[0]} is here` : `${friends[0]} + ${friends.length - 1} are here`}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
